import { ReactElement, ReactNode, useMemo } from 'react';
import {
    FetchTasksQuery,
    useEnableTasksSockets,
    useFetchTasks
} from '../../queries';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '../task-filter-provider';
import { TasksContext } from './context';
import {
    getStartOfDayInTimezone,
    getEndOfDayInTimezone
} from '@/common/util/format-date';

type TasksProviderProps = {
    readonly children?: ReactNode;
};
export function TasksProvider(props: TasksProviderProps): ReactElement {
    const { children } = props;

    const { company } = useCompanyContext();

    const { teamIds, userIds, deviceIds, timeRange, timezone } =
        useTaskFilter();

    const query = useMemo(() => {
        const _query: FetchTasksQuery = {
            companyId: company.id
        };
        if (teamIds.size > 0) {
            _query.teamIds = Array.from(teamIds);
        }
        if (userIds.size > 0) {
            _query.userIds = Array.from(userIds);
            _query.useNewUserSystem = true;
        }
        if (deviceIds.size > 0) {
            _query.deviceIds = Array.from(deviceIds);
        }
        if (timeRange) {
            _query.from = getStartOfDayInTimezone(timeRange.from, timezone);
            _query.to = getEndOfDayInTimezone(timeRange.to, timezone);
        }
        return _query;
    }, [company.id, teamIds, userIds, deviceIds, timeRange, timezone]);

    const {
        tasks: unfilteredTasks,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchTasks(query);

    useEnableTasksSockets(company.id);

    const tasks = useMemo(() => {
        return unfilteredTasks.sort((a, b) => {
            const statusRank = {
                ALERTED: 0,
                ACTIVE: 1,
                COMPLETED: 2
            };

            const aStatus = statusRank[a.simpleStatus];
            const bStatus = statusRank[b.simpleStatus];
            if (aStatus !== bStatus) {
                return aStatus - bStatus;
            }
            return (
                new Date(b.startTime).getTime() -
                new Date(a.startTime).getTime()
            );
        });
    }, [unfilteredTasks]);

    return (
        <TasksContext.Provider
            value={{
                tasks,
                status,
                fetchNextPage,
                isFetchingNextPage,
                hasNextPage,
                refetch
            }}
        >
            {children}
        </TasksContext.Provider>
    );
}
